import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "./store";
import { hasBranchSwitchingPermission } from "../types/user";

// ==================== Auth Selectors ====================

export const selectAuth = (state: RootState) => state.auth;

export const selectIsAuthenticated = createSelector(
  [selectAuth],
  (auth) => auth.isAuthenticated
);

export const selectUser = createSelector(
  [selectAuth],
  (auth) => auth.user
);

export const selectAuthStatus = createSelector(
  [selectAuth],
  (auth) => auth.status
);

export const selectAuthError = createSelector(
  [selectAuth],
  (auth) => auth.error
);

// ==================== Forms Selectors ====================

export const selectForms = (state: RootState) => state.forms;

export const selectFormDataByScreen = createSelector(
  [selectForms, (state: RootState, screenId: string) => screenId],
  (forms, screenId) => forms.formDataByScreen[screenId] || {}
);

export const selectScreenLoading = createSelector(
  [selectForms, (state: RootState, screenId: string) => screenId],
  (forms, screenId) => forms.loadingByScreen[screenId] || false
);

export const selectFormInstanceCounter = createSelector(
  [selectForms],
  (forms) => forms.formInstanceCounter
);

// ==================== Layout Selectors ====================

export const selectLayout = (state: RootState) => state.layout;

export const selectIsSideMenuOpen = createSelector(
  [selectLayout],
  (layout) => layout.isSideMenuOpen
);

export const selectIsMobile = createSelector(
  [selectLayout],
  (layout) => layout.isMobile
);

export const selectIsTablet = createSelector(
  [selectLayout],
  (layout) => layout.isTablet
);

export const selectCurrentBranch = createSelector(
  [selectLayout],
  (layout) => layout.currentBranch
);

export const selectAvailableBranches = createSelector(
  [selectLayout],
  (layout) => layout.availableBranches
);

export const selectBranchSwitchUiEnabled = createSelector(
  [selectLayout],
  (layout) => layout.branchSwitchUiEnabled
);

/** User permission + Settings session toggle (both required for header branch dropdown). */
export const selectCanShowBranchSelector = createSelector(
  [selectUser, selectBranchSwitchUiEnabled],
  (user, branchSwitchUiEnabled) =>
    hasBranchSwitchingPermission(user) && branchSwitchUiEnabled
);

// ==================== Navigation Selectors ====================

export const selectNavigation = (state: RootState) => state.navigation;

export const selectCurrentView = createSelector(
  [selectNavigation],
  (navigation) => navigation.currentView
);

export const selectPreviousView = createSelector(
  [selectNavigation],
  (navigation) => navigation.previousView
);

// ==================== Notifications Selectors ====================

export const selectNotifications = (state: RootState) => state.notifications;

export const selectCurrentNotification = createSelector(
  [selectNotifications],
  (notifications) => notifications.current
);

// ==================== Lookups Selectors ====================

export const selectLookups = (state: RootState) => state.lookups;

export const selectLookupOptions = createSelector(
  [selectLookups, (state: RootState, lookupKey: string) => lookupKey],
  (lookups, lookupKey) => lookups.lookups[lookupKey] || []
);

export const selectLookupLoading = createSelector(
  [selectLookups, (state: RootState, lookupKey: string) => lookupKey],
  (lookups, lookupKey) => lookups.loading[lookupKey] || false
);

export const selectLookupError = createSelector(
  [selectLookups, (state: RootState, lookupKey: string) => lookupKey],
  (lookups, lookupKey) => lookups.errors[lookupKey] || null
);

export const selectLookupLastFetched = createSelector(
  [selectLookups, (state: RootState, lookupKey: string) => lookupKey],
  (lookups, lookupKey) => lookups.lastFetched[lookupKey] || 0
);

